import type { Card, ClientIntent, SeatView, TableSnapshot } from '@tctp/protocol';
import { type HandRecord, LIMITS } from '@tctp/protocol';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ServerClock } from '../clock';
import { type Highlight, bestFive, tilts } from '../bestFive';
import { bubbles } from '../bubbles';
import { equity } from '../equity';
import { handTag } from '../handTag';
import { rememberNickname, rememberedNickname } from '../identity';
import { armAudio, dealSound } from '../notify';
import { cycleSoundLevel, soundEnabled, soundLevel } from '../sound';
import { DEAL_MS, DEAL_STEP_MS, FLIP_MS, MONITOR_BAR_PX, REVEAL_MS, dealOrder, geometryFor, positionFor, useLayout, STREET_GAP_MS } from '../stage';
import { useActionSounds } from '../useActionSounds';
import { useBoardDeal } from '../useBoardDeal';
import { useClockWarning } from '../useClockWarning';
import { useCountdown } from '../useCountdown';
import { useLandedBoard } from '../useLandedBoard';
import { useSettled } from '../useSettled';
import { useShortcuts } from '../useShortcuts';
import { useTurnNotification } from '../useTurnNotification';
import type { Verification } from '../useVerification';
import { ActionArea } from './ActionArea';
import { BetChip } from './BetChip';
import { ChatStrip } from './ChatStrip';
import { EmptySeat } from './EmptySeat';
import { Felt } from './Felt';
import { GameConfigModal } from './GameConfigModal';
import { JoinRequestModal } from './JoinRequestModal';
import { Modal } from './Modal';
import { OptionsMenu } from './OptionsMenu';
import { SeatBox } from './SeatBox';
import { SeatBubble } from './SeatBubble';
import { type SessionTab, SessionModal } from './SessionModal';
import { SitModal } from './SitModal';
import { Stage } from './Stage';
import { TopLeftBar } from './TopLeftBar';
import { TopRightInfo } from './TopRightInfo';
import { TopUpModal } from './TopUpModal';
import { VerifyModal } from './VerifyModal';

type ModalKind = 'sit' | 'topUp' | 'config' | 'session' | 'verify' | 'request' | 'leave' | null;

const BETTING = new Set(['fold', 'check', 'call', 'raise']);

/** `monitor`: the operator's read-only view — no sitting, no chat; face-up cards come from the snapshot itself. */
export function TableView({ snapshot, history = [], verification, clock, act, roomId, monitor = false }: {
  snapshot: TableSnapshot; history?: HandRecord[]; verification: Verification; clock: ServerClock; act: (intent: ClientIntent) => Promise<void>; roomId: string; monitor?: boolean;   // `history`: completed hands, kept by useRoom from the history events (the monitor has none)
}) {
  const { you, hand, seats } = snapshot;
  const mySeat = you.seat;
  const me = mySeat === null ? null : seats[mySeat] ?? null;

  const [modal, setModal] = useState<ModalKind>(null);
  const [sitSeat, setSitSeat] = useState<number | null>(null);
  const [requestedSeat, setRequestedSeat] = useState<number | null>(null);
  const [sessionTab, setSessionTab] = useState<SessionTab>('log');
  const [options, setOptions] = useState(false);
  const [pick, setPick] = useState<Card | null>(null);
  const [ignored, setIgnored] = useState<string[]>([]);
  const [sound, setSound] = useState(soundLevel);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSeen, setChatSeen] = useState(0);   // id of the newest message when the portrait chat sheet was last opened or closed
  const layout = useLayout(monitor ? MONITOR_BAR_PX : 0);
  const g = geometryFor(layout);

  useEffect(() => { setPick(null); }, [hand?.handId, me?.discardLocked]);
  useEffect(() => { if (mySeat !== null || you.pendingRequest === null) setRequestedSeat(null); }, [mySeat, you.pendingRequest === null]);
  useEffect(() => { if (modal === 'request' && snapshot.requests.length === 0) setModal(null); }, [modal, snapshot.requests.length]);

  const actionRemaining = useCountdown(hand?.actionDeadline ?? null, clock);
  const discardRemaining = useCountdown(hand?.discardDeadline ?? null, clock);
  const ritRemaining = useCountdown(hand?.runItTwice?.deadline ?? null, clock);
  const picking = !monitor && hand?.phase === 'discard' && me !== null && me.inHand && !me.folded && !me.discardLocked;   // a replay shows your seat as yours but never asks you to pick
  const myTurn = snapshot.legalActions !== null || picking;
  useTurnNotification(myTurn, 'Pineapple Poker');
  useClockWarning(picking ? discardRemaining : actionRemaining, myTurn);

  const showdown = hand !== null && (hand.phase === 'showdown' || hand.phase === 'complete');
  const dealing = hand !== null && hand.phase === 'discard';
  const order = useMemo(() => (hand ? dealOrder(seats.map(s => s !== null && s.inHand), hand.buttonSeat) : []), [hand?.handId, hand?.buttonSeat, seats]);
  const dealCount = order.filter(i => i !== null).length;
  // DISCARD and the card picks wait until the viewer's third card has landed and turned face-up, as on PokerNow.
  const myDealIndex = dealing && mySeat !== null ? order[mySeat] ?? null : null;
  const dealKey = myDealIndex !== null ? hand!.handId : null;
  const [landedKey, setLandedKey] = useState<string | null>(null);
  useEffect(() => {
    if (dealKey === null || myDealIndex === null) return;
    const t = window.setTimeout(() => setLandedKey(dealKey), (2 * dealCount + myDealIndex) * DEAL_STEP_MS + DEAL_MS + FLIP_MS);
    return () => window.clearTimeout(t);
    // the delay is read when the deal starts; dealCount cannot change during a deal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealKey]);
  const cardsLanded = dealKey === null || landedKey === dealKey;
  useEffect(() => {
    if (!hand || hand.phase !== 'discard' || !soundEnabled()) return;
    // One deal sound per round of cards (three per hand), not per card: nine overlapping plays for a three-way deal were a burst.
    const timers = Array.from({ length: 3 }, (_, round) => window.setTimeout(dealSound, round * dealCount * DEAL_STEP_MS + DEAL_MS / 2));
    return () => timers.forEach(t => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hand?.handId, hand?.phase === 'discard', dealCount]);
  const boardLen = hand?.board.length ?? 0;
  // Board cards that arrive with the showdown (an all-in run-out) wait for the hands to flip face-up first.
  const { delays: boardDelays, fresh: freshBoard } = useBoardDeal(hand?.handId ?? null, hand?.board ?? [], showdown ? REVEAL_MS : 0);
  // Run it twice: run 2's own cards (after the shared ones) deal one street gap after run 1's last card has landed.
  const board2Own = hand?.board2 ? hand.board2.slice(hand.runOutFrom) : [];
  const run1EndMs = freshBoard.length > 0 ? Math.max(...freshBoard) + DEAL_MS + STREET_GAP_MS : 0;
  // Keyed by the hand from its start, not from the moment board 2 appears: a key that changes as the cards arrive would read them as already dealt.
  const { delays: board2Delays, fresh: freshBoard2 } = useBoardDeal(hand ? `${hand.handId}:2` : null, board2Own, run1EndMs);
  const freshAll = [...freshBoard, ...freshBoard2];
  // Run 1 fades behind run 2 only once run 2's first card is on its way (at once on a reload that shows both boards).
  const run2Started = useSettled(hand?.board2 != null, freshBoard2[0] ?? 0);
  const boardDoneMs = freshAll.length > 0 ? Math.max(...freshAll) + DEAL_MS : 0;
  useEffect(() => {
    if (freshAll.length === 0 || !soundEnabled()) return;
    const timers = freshAll.map(d => window.setTimeout(dealSound, d + DEAL_MS / 2));
    return () => timers.forEach(t => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardLen, board2Own.length]);
  // A run-out arrives as one snapshot: the hands flip at once; the tags, winners, win sound and show row wait until its last board card has landed.
  const shownBoardLen = useSettled(boardLen, boardDoneMs);
  const shownShowdown = useSettled(showdown, boardDoneMs);
  // Board cards still landing (a street, or an all-in run-out): the host cannot pause into the middle of it; PAUSE and P wait for the board.
  const landing = hand !== null && (shownBoardLen !== boardLen || shownShowdown !== showdown);
  // Win-probability badges during an all-in run-out (the last betting action came before the river): per tabled hand, per street landed.
  const landedBoardLen = useLandedBoard(hand?.handId ?? null, boardLen, freshBoard);
  const landedStreet = landedBoardLen < 3 ? 0 : landedBoardLen;   // preflop, then the flop as a whole: the sampled preflop path runs once
  const landed2 = useLandedBoard(hand ? `${hand.handId}:2` : null, board2Own.length, freshBoard2);
  // Run twice: the badges follow the board being dealt — run 1's while it lands, then run 2's (with run 1's own cards out of the deck). No run prefix: "#1 95%" reads as 195.
  const second = hand?.board2 != null && run2Started;
  const equities = useMemo(() => {
    if (!hand || !showdown || hand.board.length < 5) return null;
    if ((hand.actions.filter(a => BETTING.has(a.type)).at(-1)?.street ?? 'preflop') === 'river') return null;   // a river showdown: nothing was left to come
    const tabled = seats.filter((s): s is SeatView => s !== null && s.inHand && !s.folded && s.shown.length === 2);
    if (tabled.length < 2) return null;
    const discards = seats.flatMap(s => (s && s.discard !== null ? [s.discard] : []));
    const landed = second && hand.board2 ? hand.board2.slice(0, hand.runOutFrom + landed2) : hand.board.slice(0, landedStreet);
    const board = landed.length < 3 ? [] : landed;
    const dead = second ? [...discards, ...hand.board.slice(hand.runOutFrom)] : discards;
    const e = equity(tabled.map(s => s.shown), board, dead);
    return new Map(tabled.map((s, i) => [s.seat, Math.round(e[i]! * 100)]));
  }, [hand, showdown, seats, landedStreet, landed2, second]);
  useActionSounds(hand, shownShowdown, me, seats);
  const winners = useMemo(() => new Set((hand?.results?.awards ?? []).flatMap(a => a.shares.map(s => s.seat))), [hand?.results]);
  // The tilt is decorative and must be drawn once per hand, as `tilts` says. The glow itself has to be recomputed
  // whenever a seat changes (someone shows a card, and more cards join the winning hand), and a snapshot arrives with
  // fresh arrays every time anyone at the table does anything at all -- rabbit hunting, showing, chatting. Drawing the
  // angles inside that recompute made every winning card jump to a new angle on each of those, which is what players
  // saw as the table flickering. Keyed by hand, so a card keeps its angle for as long as it is on screen.
  const tiltJar = useRef<{ handId: string | null; angles: Map<Card, number> }>({ handId: null, angles: new Map() });
  if (tiltJar.current.handId !== (hand?.handId ?? null)) tiltJar.current = { handId: hand?.handId ?? null, angles: new Map() };
  const tiltsFor = (cards: Set<Card>): Map<Card, number> => {
    const jar = tiltJar.current.angles;
    for (const [card, angle] of tilts([...cards].filter(c => !jar.has(c)))) jar.set(card, angle);
    return new Map([...cards].map(c => [c, jar.get(c) ?? 0]));
  };
  // Once the reveal has landed, the cards that make each winning hand (a winner's public keepers + the board) lift with a random tilt; the board cards outside it fade, while players' cards keep their brightness.
  // Run twice: each board glows for its own run's winners; a player's kept cards glow if either run used them.
  const glowFor = (run: 1 | 2 | undefined, board: Card[]): Highlight | null => {
    if (!hand || !shownShowdown || board.length === 0) return null;
    const runWinners = new Set((hand.results?.awards ?? []).filter(a => run === undefined || a.run === run).flatMap(a => a.shares.map(s => s.seat)));
    const used = new Set<Card>();
    for (const seat of runWinners) {
      const v = seats[seat]; if (!v) continue;
      const keepers = seat === mySeat ? v.keepers : v.shown.length === 2 ? v.shown : null;
      if (keepers) for (const c of bestFive([...keepers, ...board])) used.add(c);
    }
    return used.size > 0 ? { used, tilt: tiltsFor(used) } : null;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const highlight = useMemo<Highlight | null>(() => glowFor(hand?.board2 ? 1 : undefined, hand?.board ?? []), [hand?.handId, shownShowdown, winners, seats, mySeat]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const highlight2 = useMemo<Highlight | null>(() => (hand?.board2 ? glowFor(2, hand.board2) : null), [hand?.handId, shownShowdown, winners, seats, mySeat]);
  const seatGlow = useMemo<Highlight | null>(() => {
    if (!highlight2) return highlight;
    const used = new Set([...(highlight?.used ?? []), ...highlight2.used]);
    return { used, tilt: new Map([...(highlight?.tilt ?? []), ...highlight2.tilt]) };
  }, [highlight, highlight2]);
  const wonBySeat = useMemo(() => {
    const m = new Map<number, number>();
    for (const a of hand?.results?.awards ?? []) for (const s of a.shares) m.set(s.seat, (m.get(s.seat) ?? 0) + s.amount);
    return m;
  }, [hand?.results]);
  const seatBubbles = useMemo(() => (hand ? bubbles(hand, seats) : new Map<number, string>()), [hand, seats]);
  // Stacks, chips in front and the pot (its total and its breakdown) are held at their pre-result values until the run-out board has landed, so nothing spoils it.
  // The hold ends at a deadline fixed when the board arrived: a later snapshot (chat, a join) must not cut it short.
  // A snapshot is parsed fresh off the socket, so `seats` is a new array every time anyone at the table does anything,
  // even when not one number in it changed. Keying the hold on that identity re-armed it for a single render on every
  // such message, and that one render painted a finished hand as live again -- which is how the pot pill came to show
  // `pot - chips still in front` with the pot already cleared. Hold on what the numbers are, not on the array they came in.
  const liveSig = JSON.stringify([hand?.potTotal ?? 0, hand?.pots ?? [], seats.map(s => s && [s.stack, s.busted, s.committedThisStreet, s.totalCommitted])]);
  const liveRef = useRef<{ sig: string; value: { seats: typeof seats; pot: number; pots: NonNullable<typeof hand>['pots'] } } | null>(null);
  if (liveRef.current === null || liveRef.current.sig !== liveSig) liveRef.current = { sig: liveSig, value: { seats, pot: hand?.potTotal ?? 0, pots: hand?.pots ?? [] } };
  const live = liveRef.current.value;
  const holdUntil = useRef(0);
  if (freshBoard.length > 0) holdUntil.current = Date.now() + boardDoneMs;
  const held = useSettled(live, Math.max(0, holdUntil.current - Date.now()));
  // An all-in run-out settles the chips the way a dealer does before running the board: every contested chip is in front of
  // its owner, the uncalled excess is back behind the player who bet it, and the pot is what will actually be fought over.
  // Without this the felt keeps the frame before the last call, whose total disagrees with the breakdown under the pill.
  const settled = useMemo(() => {
    const awards = hand?.results?.awards ?? [];
    if (held === live || awards.length === 0) return null;
    const pot = awards.reduce((n, a) => n + a.amount, 0);
    let front = 0;
    let coherent = true;
    const out = seats.map((s, i) => {
      const h = held.seats[i];
      if (!s || !h) return s;
      const earlier = h.totalCommitted - h.committedThisStreet;   // already collected, from the streets before this one
      const inFront = s.totalCommitted - earlier;                 // this street's contested chips; the server has netted the refund out
      if (earlier < 0 || inFront < 0) coherent = false;
      front += inFront;
      return { ...s, stack: h.stack - (inFront - h.committedThisStreet), busted: h.busted, committedThisStreet: inFront };
    });
    // Only when the numbers add up: the chips in front plus what was already collected are exactly the contested pot.
    return coherent && front <= pot && out.every(s => !s || s.stack >= 0) ? { pot, seats: out } : null;
  }, [hand?.results, held, live, seats]);
  const shownSeats = useMemo(() => (settled ? settled.seats : held === live ? seats : seats.map((s, i) => {
    const h = held.seats[i];
    return s && h ? { ...s, stack: h.stack, busted: h.busted, committedThisStreet: h.committedThisStreet } : s;
  })), [held, live, seats, settled]);
  const shownPot = settled ? settled.pot : held === live ? live.pot : held.pot;
  // The breakdown under the pill during the hold: a finished hand's awards, grouped by pot (a pot run twice is awarded once per run), since the
  // held snapshot predates the last call and so has no pots yet; otherwise (a street dealing mid-hand) the held pots.
  const shownPots = useMemo(() => {
    const byPot = new Map<number, number>();
    if (held !== live) for (const a of hand?.results?.awards ?? []) byPot.set(a.potIndex, (byPot.get(a.potIndex) ?? 0) + a.amount);
    return byPot.size > 1 ? [...byPot].sort((a, b) => a[0] - b[0]).map(([, amount]) => ({ amount, eligible: [] })) : held.pots;
  }, [hand?.results, held, live]);
  // While the run-out is landing the hand is still shown as live: the pot keeps its total and the end-of-hand labels wait.
  const shownHand = useMemo(() => (hand && held !== live ? { ...hand, potTotal: shownPot, pots: shownPots, phase: hand.phase === 'complete' ? 'showdown' as const : hand.phase } : hand), [hand, held, live, shownPot, shownPots]);
  const collected = hand ? Math.max(0, shownPot - shownSeats.reduce((n, s) => n + (s?.committedThisStreet ?? 0), 0)) : 0;   // never a negative pot, whatever disagrees
  const canSit = !monitor && mySeat === null && you.pendingRequest === null && !snapshot.ended;
  const pendingRequests = you.isHost ? snapshot.requests.filter(r => !ignored.includes(r.id)) : [];
  const request = modal === 'request' ? snapshot.requests[0] ?? null : pendingRequests[0] ?? null;
  const shortcutsEnabled = modal === null && !options && request === null;

  const toggleCard = useCallback((card: Card) => {
    setPick(prev => (prev === card ? null : card));
  }, []);
  const discard = useCallback(() => {
    if (pick === null) return;
    void act({ type: 'discard', card: pick });
    setPick(null);
  }, [pick, act]);
  const toggleSound = useCallback(() => { setSound(cycleSoundLevel()); }, []);
  const openSession = useCallback((tab: SessionTab) => { setSessionTab(tab); setModal('session'); setOptions(false); }, []);
  const setChat = (open: boolean): void => { setChatOpen(open); setChatSeen(snapshot.chat.at(-1)?.id ?? 0); };
  // By id, not length: the server trims the history to LIMITS.chatHistory. Your own messages are never unread — on a phone the sheet
  // closes as you send, so the echo used to come back and badge itself.
  const unread = chatOpen ? 0 : snapshot.chat.filter(m => m.id > chatSeen && m.nickname !== you.nickname).length;
  const shortcuts = useMemo(() => ({
    l: () => openSession('log'),
    p: you.isHost && snapshot.running && (snapshot.paused || !landing) ? () => void act({ type: snapshot.paused ? 'resume' : 'pause' }) : undefined,
  }), [openSession, you.isHost, snapshot.running, snapshot.paused, landing, act]);
  useShortcuts(shortcuts, shortcutsEnabled);

  /** Hand names for the seat's pill: one line, or `#1 …` and `#2 …` once a twice run has landed. */
  function tagsFor(seat: number): string[] {
    const v = seats[seat];
    if (!v || !hand || !v.inHand || v.folded) return [];
    const twice = hand.board2 !== null && shownShowdown;
    if (seat === mySeat) {
      if (!v.keepers || shownBoardLen < 3) return [];
      const first = handTag([...v.keepers, ...hand.board.slice(0, shownBoardLen)]);
      if (!twice) return first ? [first] : [];
      const second = handTag([...v.keepers, ...hand.board2!]);
      return [`#1 ${first}`, `#2 ${second}`];
    }
    if (!shownShowdown || !v.handName) return [];
    return twice && v.handName2 ? [`#1 ${v.handName.toUpperCase()}`, `#2 ${v.handName2.toUpperCase()}`] : [v.handName.toUpperCase()];
  }

  return (
    <>
      <Stage layout={layout} className={shownHand?.board2 ? 'two-boards' : undefined}>   {/* the phone chat sheet starts lower while two boards are showing */}
        <div className="stage-click" onClickCapture={armAudio} style={{ inset: 0 }}>
          <TopLeftBar seated={mySeat !== null} away={me?.away ?? false} pendingCount={you.isHost ? snapshot.requests.length : 0}
            onOptions={() => setOptions(o => !o)} onLeave={() => setModal('leave')} onAway={() => void act({ type: 'setAway', away: !me?.away })} onTopUp={() => setModal('topUp')} />
          <TopRightInfo hostNickname={snapshot.hostNickname} config={snapshot.config} isHost={you.isHost} running={snapshot.running} paused={snapshot.paused} canPause={!landing} endRequested={snapshot.endRequested}
            sound={sound} onToggleSound={toggleSound} onPause={() => void act({ type: 'pause' })} onResume={() => void act({ type: 'resume' })} onEnd={() => void act({ type: 'endSession' })} />
          <Felt hand={shownHand} collected={collected} verification={verification} boardDelays={boardDelays} board2Delays={board2Delays} behind={run2Started} highlight={highlight} highlight2={highlight2} ritRemainingMs={hand?.runItTwice ? ritRemaining : null} />
          {shownSeats.map((view, seat) => {
            if (!view && mySeat !== null) return null;   // once seated, empty seats are not drawn (PokerNow's table hides them)
            const pos = positionFor(seat, mySeat);
            const at = g.seats[pos]!;
            return (
              <div key={seat} className={`at pos-${pos}${!layout.portrait && (pos === 4 || pos === 5) ? ' top-row' : ''}`} style={{ left: at.x, top: at.y }} data-testid={`seat-slot-${pos}`}>
                {view
                  ? <SeatBox view={view} isMe={seat === mySeat} acting={hand?.actingSeat === seat && hand.phase !== 'complete'} remainingMs={actionRemaining}
                      totalMs={hand?.extraTime ? snapshot.config.timeBankMs : snapshot.config.actionTimerMs ?? 0} extraTime={hand?.extraTime ?? false} winner={shownShowdown && winners.has(seat)} won={shownShowdown ? wonBySeat.get(seat) ?? null : null} equity={equities?.get(seat) ?? null} highlight={seatGlow} showdown={showdown} tags={tagsFor(seat)} handId={hand?.handId ?? null}
                      marked={seat === mySeat ? pick : null} onToggleCard={seat === mySeat && picking && cardsLanded ? toggleCard : undefined}
                      dealIndex={dealing ? order[seat] ?? null : null} dealCount={dealCount} dealFrom={{ x: g.centre.x - at.x, y: g.centre.y - at.y }} portrait={layout.portrait} />
                  : <EmptySeat seat={seat} canSit={canSit} onSit={s => { setSitSeat(s); setModal('sit'); }}
                      pendingNickname={requestedSeat === seat && you.pendingRequest?.kind === 'sit' ? rememberedNickname() || 'You' : null} />}
              </div>
            );
          })}
          {!hand?.board2 && shownSeats.map((view, seat) => view && view.committedThisStreet > 0 && <BetChip key={`chip-${seat}`} amount={view.committedThisStreet} at={g.chips[positionFor(seat, mySeat)]!} />)}   {/* two boards fill the felt: the all-in chips are in the pot's total */}
          {[...seatBubbles].map(([seat, text]) => <SeatBubble key={`bubble-${seat}`} text={text} at={g.bubbles[positionFor(seat, mySeat)]!} />)}
          {layout.portrait && <button type="button" className="log-btn chat-btn" onClick={() => setChat(true)}>{unread > 0 ? `CHAT (${unread})` : 'CHAT'}</button>}
          <button type="button" className="log-btn" onClick={() => openSession('log')}><span className="hint" aria-hidden="true">L</span>{layout.portrait ? 'LOG' : 'LOG / LEDGER'}</button>
          {(!layout.portrait || chatOpen) && (
            <ChatStrip chat={snapshot.chat} canChat={!monitor && mySeat !== null} act={act} shortcutsEnabled={shortcutsEnabled} onClose={layout.portrait ? () => setChat(false) : undefined} />
          )}
          {!monitor && <ActionArea snapshot={held === live ? snapshot : { ...snapshot, seats: shownSeats, hand: shownHand }} act={act} discardRemainingMs={discardRemaining} hasPick={pick !== null} cardsLanded={cardsLanded} onDiscard={discard} onTopUp={() => setModal('topUp')} shortcutsEnabled={shortcutsEnabled} finished={shownShowdown} />}
          {options && (
            <OptionsMenu nickname={you.nickname} isHost={you.isHost} seated={mySeat !== null} away={me?.away ?? false} pendingCount={snapshot.requests.length} sound={sound} verification={verification.status}
              canShuffle={hand === null || (hand.phase === 'complete' && !landing)}
              onToggleSound={toggleSound} onVerify={() => { setOptions(false); setModal('verify'); }} onGameConfig={() => { setOptions(false); setModal('config'); }} onRequests={() => { setOptions(false); setIgnored([]); setModal('request'); }}
              onShuffleSeats={() => { setOptions(false); void act({ type: 'shuffleSeats' }); }}
              onTopUp={() => { setOptions(false); setModal('topUp'); }} onAway={() => { setOptions(false); void act({ type: 'setAway', away: !me?.away }); }} onLeave={() => { setOptions(false); setModal('leave'); }} onClose={() => setOptions(false)} />
          )}
        </div>
      </Stage>
      {modal === 'sit' && sitSeat !== null && (
        <SitModal seat={sitSeat} defaultStack={snapshot.config.defaultStack} maxStack={LIMITS.stackMultiple * snapshot.config.defaultStack} initialNickname={rememberedNickname()}
          onClose={() => setModal(null)}
          onSubmit={({ nickname, stack }) => { rememberNickname(nickname); setRequestedSeat(sitSeat); void act({ type: 'sit', seat: sitSeat, nickname, stack }); setModal(null); }} />
      )}
      {modal === 'leave' && (
        <Modal title="Leave the seat?" onClose={() => setModal(null)}>
          <p className="modal-text">Your chips go back to the ledger and the seat opens for someone else. You can sit down again while the game is running.</p>
          <div className="modal-actions">
            <button type="button" className="btn grey" onClick={() => setModal(null)}>STAY</button>
            <button type="button" className="btn red" onClick={() => { void act({ type: 'standUp' }); setModal(null); }}>LEAVE SEAT</button>
          </div>
        </Modal>
      )}
      {modal === 'topUp' && <TopUpModal maxAmount={LIMITS.stackMultiple * snapshot.config.defaultStack} onClose={() => setModal(null)} onSubmit={amount => { void act({ type: 'topUp', amount }); setModal(null); }} />}
      {modal === 'config' && <GameConfigModal snapshot={snapshot} act={act} onClose={() => setModal(null)} />}
      {modal === 'session' && <SessionModal snapshot={snapshot} history={history} roomId={roomId} initialTab={sessionTab} compact={layout.portrait} onClose={() => setModal(null)} />}
      {modal === 'verify' && <VerifyModal verification={verification} commitmentCount={hand?.commitment.length ?? 0} onClose={() => setModal(null)} />}
      {request && (modal === null || modal === 'request') && (
        <JoinRequestModal request={request} act={act} onIgnore={() => { setIgnored(ids => [...ids, request.id]); setModal(null); }} />
      )}
    </>
  );
}
